import React, { useRef, useEffect, useState } from "react";

function RevealOnScroll({
  children,
  threshold = [0.2], // Default to reveal at 20% visibility
  triggerOnce = true,
  delay = 0,
  rootMargin = "0px",
}) {
  const childRefs = useRef([]);
  const [visibleStates, setVisibleStates] = useState([]);
  const timeoutRefs = useRef([]);

  const childrenArray = React.Children.toArray(children);

  // Create a ref for each child
  if (childRefs.current.length !== childrenArray.length) {
    childRefs.current = childrenArray.map(() => React.createRef());
    timeoutRefs.current = Array(childrenArray.length).fill(null);
  }

  // Initialize visibility states (one for each child)
  useEffect(() => {
    setVisibleStates(Array(childrenArray.length).fill(false));
  }, [childrenArray.length]);

  useEffect(() => {
    const handleIntersect = (entries, observer) => {
      entries.forEach((entry) => {
        const index = childRefs.current.findIndex(
          (ref) => ref.current === entry.target
        );
        if (index !== -1) {
          // Clear any existing timeout for this index
          if (timeoutRefs.current[index]) {
            clearTimeout(timeoutRefs.current[index]);
          }

          const isIntersecting = entry.isIntersecting && entry.intersectionRatio >= threshold[0];
          
          // Set a new timeout
          timeoutRefs.current[index] = setTimeout(() => {
            setVisibleStates((prev) => {
              const newStates = [...prev];
              newStates[index] = isIntersecting;
              return newStates;
            });
            
            if (triggerOnce && isIntersecting) {
              observer.unobserve(entry.target);
            }
          }, delay);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, {
      threshold: Array.isArray(threshold) ? threshold : [threshold],
      rootMargin,
    });

    childRefs.current.forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => {
      // Clear all timeouts on cleanup
      timeoutRefs.current.forEach((timeout) => {
        if (timeout) clearTimeout(timeout);
      });
      childRefs.current.forEach((ref) => {
        if (ref.current) observer.unobserve(ref.current);
      });
    };
  }, [threshold, triggerOnce, delay, childrenArray.length]);

  return (
    <>
      {childrenArray.map((child, index) => {
        const isVisible = visibleStates[index];
        const childDelay = delay;

        return (
          <div
            key={index}
            ref={childRefs.current[index]}
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(20px)",
              transition: `opacity 0.6s ease ${childDelay}ms, transform 0.6s ease ${childDelay}ms`,
              willChange: 'opacity, transform'
            }}
          >
            {child}
          </div>
        );
      })}
    </>
  );
}

export default RevealOnScroll;
